import { useState, useEffect, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import { format, addDays } from 'date-fns'
import { ru } from 'date-fns/locale'
import Button from '@/components/ui/Button'
import NumberStepper from '@/components/ui/NumberStepper'
import SlotParticipantsSheet from '@/components/trainer/SlotParticipantsSheet'
import {
  getTimeSlots,
  generateSlots,
  blockSlot,
  unblockSlot,
  createSlot,
  deleteSlot,
  updateSlotCapacity,
  TimeSlot,
} from '@/services/schedule'
import { trainerCancelBooking, createGuestBooking, updateGuestBooking } from '@/services/bookings'

export default function TrainerSchedule() {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date())
  const [slots, setSlots] = useState<TimeSlot[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isGenerating, setIsGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null)
  const [cancellingBookingId, setCancellingBookingId] = useState<string | null>(null)
  const cancellationPending = useRef(false)
  const closeParticipants = useCallback(() => setSelectedSlotId(null), [])

  // Модальное окно добавления слота
  const [showAddModal, setShowAddModal] = useState(false)
  const [newSlot, setNewSlot] = useState({ start_time: '09:00', end_time: '10:00', max_clients: 1 })
  const [isAddingSlot, setIsAddingSlot] = useState(false)

  // Генерируем 7 дней начиная с сегодня
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(new Date(), i))

  // Загрузка слотов
  const loadSlots = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const dateFrom = format(weekDays[0], 'yyyy-MM-dd')
      const dateTo = format(weekDays[6], 'yyyy-MM-dd')
      const data = await getTimeSlots(dateFrom, dateTo)
      setSlots(data.slots)
    } catch (err: any) {
      console.error('Error loading slots:', err)
      setError(err.response?.data?.detail || 'Ошибка загрузки расписания')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadSlots()
  }, [loadSlots])

  // Refetch when app becomes visible (Telegram Mini App caching)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        loadSlots()
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [loadSlots])

  // Генерация слотов на неделю
  const handleGenerateSlots = async () => {
    setIsGenerating(true)
    try {
      const dateFrom = format(new Date(), 'yyyy-MM-dd')
      const dateTo = format(addDays(new Date(), 7), 'yyyy-MM-dd')
      const result = await generateSlots(dateFrom, dateTo)
      if (result.created === 0) {
        alert(result.message === 'No active templates found'
          ? 'Нет активных интервалов.\n\nОткройте «Настройки» → «Расписание недели» и создайте расписание или включите нужные интервалы.'
          : 'Новых слотов нет: подходящие слоты уже созданы или время их начала прошло.')
      } else {
        alert(`Создано ${result.created} слотов`)
      }
      loadSlots()
    } catch (err: any) {
      console.error('Error generating slots:', err)
      alert(err.response?.data?.detail || 'Ошибка генерации слотов')
    } finally {
      setIsGenerating(false)
    }
  }

  // Блокировка/разблокировка слота
  const handleToggleBlock = async (slot: TimeSlot) => {
    try {
      if (slot.status === 'blocked') {
        await unblockSlot(slot.id)
      } else {
        await blockSlot(slot.id)
      }
      loadSlots()
    } catch (err: any) {
      console.error('Error toggling slot:', err)
      alert(err.response?.data?.detail || 'Ошибка')
    }
  }

  // Добавление слота вручную
  const handleAddSlot = async () => {
    setIsAddingSlot(true)
    try {
      await createSlot({
        date: format(selectedDate, 'yyyy-MM-dd'),
        start_time: newSlot.start_time,
        end_time: newSlot.end_time,
        max_clients: newSlot.max_clients,
      })
      setShowAddModal(false)
      setNewSlot({ start_time: '09:00', end_time: '10:00', max_clients: 1 })
      loadSlots()
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Ошибка создания слота')
    } finally {
      setIsAddingSlot(false)
    }
  }

  // Удаление слота (всегда с force после подтверждения пользователем)
  const handleDeleteSlot = async (slot: TimeSlot) => {
    const names = slot.bookings?.map(b => b.client_name).join(', ')

    const message = names
      ? `На этот слот записаны: ${names}.\nОтменить записи и удалить слот?`
      : 'Удалить этот слот?'

    if (!confirm(message)) return

    try {
      // Всегда force=true — пользователь уже подтвердил через confirm()
      await deleteSlot(slot.id, true)
      loadSlots()
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Ошибка удаления')
    }
  }

  // Одна отмена всегда относится к одной выбранной записи.
  const handleCancelBooking = async (slot: TimeSlot, booking: NonNullable<TimeSlot['bookings']>[number]) => {
    if (cancellationPending.current) return
    if (!confirm(`Отменить запись ${booking.client_name}?`)) return
    cancellationPending.current = true
    setCancellingBookingId(booking.id)
    try {
      await trainerCancelBooking(booking.id)
      setSlots(current => current.map(item => {
        if (item.id !== slot.id) return item
        const bookings = (item.bookings ?? []).filter(participant => participant.id !== booking.id)
        return { ...item, bookings, bookings_count: bookings.length,
          status: item.status === 'booked' ? 'available' : item.status }
      }))
      await loadSlots()
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Ошибка отмены')
    } finally {
      cancellationPending.current = false
      setCancellingBookingId(null)
    }
  }

  const handleCancelBookings = (slot: TimeSlot) => {
    if (slot.bookings?.length === 1) {
      void handleCancelBooking(slot, slot.bookings[0])
    } else if (slot.bookings?.length) {
      setSelectedSlotId(slot.id)
    }
  }

  // Фильтруем слоты для выбранной даты
  const selectedDateStr = format(selectedDate, 'yyyy-MM-dd')
  const filteredSlots = slots.filter((slot) => slot.date === selectedDateStr)
  const selectedSlot = slots.find((slot) => slot.id === selectedSlotId)

  return (
    <div className="p-4">
      {/* Заголовок с настройками */}
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-tg-text">Расписание</h1>
        <Link
          to="/schedule/settings"
          className="text-tg-button text-sm font-medium"
        >
          Настройки
        </Link>
      </div>

      {/* Выбор дня */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {weekDays.map((day) => {
          const isSelected = format(day, 'yyyy-MM-dd') === selectedDateStr
          return (
            <button
              key={day.toISOString()}
              onClick={() => setSelectedDate(day)}
              className={`flex-shrink-0 w-14 h-16 rounded-xl flex flex-col items-center justify-center text-sm transition-all ${
                isSelected
                  ? 'bg-tg-button text-tg-button-text'
                  : 'bg-tg-secondary text-tg-text'
              }`}
            >
              <span className="text-xs opacity-70">
                {format(day, 'EEE', { locale: ru })}
              </span>
              <span className="font-semibold">{format(day, 'd')}</span>
            </button>
          )
        })}
      </div>

      {/* Заголовок выбранного дня */}
      <h2 className="text-lg font-semibold text-tg-text mb-4">
        {format(selectedDate, 'd MMMM, EEEE', { locale: ru })}
      </h2>

      {/* Загрузка */}
      {isLoading && (
        <div className="text-center py-8 text-tg-hint">Загрузка...</div>
      )}

      {/* Ошибка */}
      {error && (
        <div className="bg-red-100 text-red-700 p-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      {/* Слоты на день */}
      {!isLoading && !error && (
        <>
          {filteredSlots.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-tg-hint mb-4">Нет слотов на этот день</p>
              <Button onClick={handleGenerateSlots} loading={isGenerating}>
                Сгенерировать слоты
              </Button>
            </div>
          ) : (
            <div className="space-y-3 mb-6">
              {filteredSlots.map((slot) => (
                <TimeSlotCard
                  key={slot.id}
                  slot={slot}
                  onOpen={() => setSelectedSlotId(slot.id)}
                  onToggleBlock={() => handleToggleBlock(slot)}
                  onDelete={() => handleDeleteSlot(slot)}
                  onCancelBookings={() => handleCancelBookings(slot)}
                  isCancelling={cancellingBookingId !== null}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Кнопки действий */}
      <div className="space-y-3 mt-4">
        <Button
          variant="secondary"
          fullWidth
          onClick={() => setShowAddModal(true)}
        >
          + Добавить слот на {format(selectedDate, 'd MMMM', { locale: ru })}
        </Button>

        {filteredSlots.length > 0 && (
          <Button
            variant="outline"
            fullWidth
            onClick={handleGenerateSlots}
            loading={isGenerating}
          >
            Сгенерировать слоты на неделю
          </Button>
        )}
      </div>

      {selectedSlot && (
        <SlotParticipantsSheet
          key={selectedSlot.id}
          slot={selectedSlot}
          onSaveCapacity={async capacity => {
            await updateSlotCapacity(selectedSlot.id, capacity)
            await loadSlots()
          }}
          onSaveGuest={async (details, bookingId) => {
            if (bookingId) await updateGuestBooking(bookingId, details)
            else await createGuestBooking(selectedSlot.id, details)
            await loadSlots()
          }}
          onClose={closeParticipants}
          onCancelBooking={booking => void handleCancelBooking(selectedSlot, booking)}
          cancellingBookingId={cancellingBookingId}
        />
      )}

      {/* Модальное окно добавления слота */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-5 w-full max-w-sm">
            <h3 className="text-lg font-semibold text-black mb-4">
              Добавить слот на {format(selectedDate, 'd MMMM', { locale: ru })}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Начало</label>
                <input
                  type="time"
                  value={newSlot.start_time}
                  onChange={(e) => {
                    const startTime = e.target.value
                    // Автоматически сдвигаем конец на +1 час
                    const [hours, mins] = startTime.split(':').map(Number)
                    const endHour = Math.min(hours + 1, 23)
                    const endTime = `${String(endHour).padStart(2, '0')}:${String(mins).padStart(2, '0')}`
                    setNewSlot({ ...newSlot, start_time: startTime, end_time: endTime })
                  }}
                  className="w-full p-3 rounded-lg border border-gray-300 text-black"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Конец</label>
                <input
                  type="time"
                  value={newSlot.end_time}
                  onChange={(e) => setNewSlot({ ...newSlot, end_time: e.target.value })}
                  className="w-full p-3 rounded-lg border border-gray-300 text-black"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Макс. клиентов</label>
                <NumberStepper
                  value={newSlot.max_clients}
                  onChange={(max_clients) => setNewSlot({ ...newSlot, max_clients })}
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowAddModal(false)}
              >
                Отмена
              </Button>
              <Button
                className="flex-1"
                onClick={handleAddSlot}
                loading={isAddingSlot}
              >
                Добавить
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Компонент слота
function TimeSlotCard({
  slot,
  onOpen,
  onToggleBlock,
  onDelete,
  onCancelBookings,
  isCancelling,
}: {
  slot: TimeSlot
  onOpen: () => void
  onToggleBlock: () => void
  onDelete: () => void
  onCancelBookings: () => void
  isCancelling: boolean
}) {
  const capacity = slot.max_clients ?? 1
  const bookingsCount = slot.bookings_count ?? slot.bookings?.length ?? 0
  const isGroup = capacity > 1
  // Older slots may have an AVAILABLE/BOOKED status out of sync with bookings.
  // Explicit blocking, completion and cancellation still take precedence.
  const status = slot.status === 'available' || slot.status === 'booked'
    ? (bookingsCount >= capacity ? 'booked' : 'available')
    : slot.status
  const bookedNames = slot.bookings?.map(b => b.client_name).join(', ')
  const capacityLabel = isGroup ? ` (${bookingsCount}/${capacity})` : ''

  const statusConfig = {
    available: {
      border: 'border-green-500',
      bg: 'bg-green-50',
      label: isGroup ? `Свободно${capacityLabel}` : 'Свободно',
    },
    booked: {
      border: 'border-blue-500',
      bg: 'bg-blue-50',
      label: bookedNames ? `Занято: ${bookedNames}${capacityLabel}` : 'Занято',
    },
    blocked: {
      border: 'border-gray-400',
      bg: 'bg-gray-100',
      label: 'Заблокировано',
    },
    completed: {
      border: 'border-purple-500',
      bg: 'bg-purple-50',
      label: 'Завершено',
    },
    cancelled: {
      border: 'border-red-400',
      bg: 'bg-red-50',
      label: 'Отменено',
    },
  }

  const config = statusConfig[status] || statusConfig.available

  return (
    <div className={`relative isolate border-l-4 rounded-lg p-4 ${config.border} ${config.bg}`}>
      <button
        type="button"
        onClick={onOpen}
        aria-label={`Посмотреть участников слота ${slot.start_time} – ${slot.end_time}`}
        aria-haspopup="dialog"
        className="absolute inset-0 rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-tg-button"
      />
      <div className="pointer-events-none flex flex-wrap justify-between items-center gap-3">
        <div className="min-w-0">
          <div className="font-medium text-gray-900">
            {slot.start_time} - {slot.end_time}
          </div>
          <div className="break-words [overflow-wrap:anywhere] text-sm text-gray-600">{config.label}</div>
          <div className="mt-1 flex items-center gap-1 text-xs text-tg-button">
            Участники
            <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m9 5 7 7-7 7" />
            </svg>
          </div>
        </div>
        <div className="pointer-events-auto relative z-10 flex flex-wrap gap-3">
          {status === 'available' && !slot.bookings?.length && (
            <button
              onClick={onToggleBlock}
              className="text-orange-500 text-sm font-medium"
            >
              Блок
            </button>
          )}
          {status === 'blocked' && (
            <button
              onClick={onToggleBlock}
              className="text-green-600 text-sm font-medium"
            >
              Разблок
            </button>
          )}
          {slot.bookings && slot.bookings.length > 0 && (
            <button
              onClick={onCancelBookings}
              disabled={isCancelling}
              className="text-orange-500 text-sm font-medium disabled:opacity-50"
            >
              Отменить запись
            </button>
          )}
          {(status === 'available' || status === 'blocked' || status === 'booked') && (
            <button
              onClick={onDelete}
              className="text-red-500 text-sm font-medium"
            >
              Удалить
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
